import { urls } from '~/preloaded';

import { get, post, put } from '~/helpers/api';
import { sendErrorNotification } from '~/web2ClientAPI/base';
import {
  sendSendApplicationErrorNotification,
  sendSendApplicationNotification,
  SEND_APPLICATION_ERRORS,
} from '~/web2ClientAPI/notifications';
import { actionsRecommendation, PURPOSES, PurposesMap, PurposesMapReversed } from '~/Actions/ActionRecommendations';

import type {
  AccountRecommendationsSettings,
  ClanRecommendation,
  ClanRecommendationsSettings,
} from '~/Actions/ActionRecommendations';
import type { AxiosCustomError } from '~/helpers/api';
import type { AppAsyncThunk } from '~/store';
import type {
  IApiGetAccountRecommendationsSettingsPayload,
  IApiGetAccountRecommendationsSettingsResponse,
  IApiGetClanRecommendationsSettingsPayload,
  IApiGetClanRecommendationsSettingsResponse,
  IApiGetLeftApplicationCountResponse,
  IApiGetRecommendationsResponse,
  IApiClanRecSendApplicationPayload,
  IApiClanRecSendApplicationResponse,
} from '~/types/api';

export const getAccountRecommendationsSettingsThunk = (): AppAsyncThunk => async (dispatch) => {
  dispatch(actionsRecommendation.getAccountRecommendationsSettings());

  try {
    const url = urls.recommendationsAccountSettings;
    const response = await get<IApiGetAccountRecommendationsSettingsResponse>(url);

    const settings: AccountRecommendationsSettings = {
      languages: response.languages,
      useVoiceChat: response.useVoiceChat === 0,
    };
    dispatch(actionsRecommendation.getAccountRecommendationsSettingsResponse(settings));
  } catch (error) {
    sendErrorNotification();
  }
};

export const setAccountRecommendationsSettingsThunk =
  (data: AccountRecommendationsSettings): AppAsyncThunk =>
  async (dispatch) => {
    dispatch(actionsRecommendation.setAccountRecommendationsSettings());

    try {
      const url = urls.recommendationsAccountSettings;
      const payload: IApiGetAccountRecommendationsSettingsPayload = {
        languages: data.languages,
        useVoiceChat: data.useVoiceChat ? 0 : 2,
      };
      const response = await put<IApiGetAccountRecommendationsSettingsResponse>(url, payload);

      const settings: AccountRecommendationsSettings = {
        languages: response.languages,
        useVoiceChat: response.useVoiceChat === 0,
      };
      dispatch(actionsRecommendation.setAccountRecommendationsSettingsResponse(settings));
    } catch (error) {
      sendErrorNotification();
    }
  };

export const getClanRecommendationsSettingsThunk = (): AppAsyncThunk => async (dispatch) => {
  dispatch(actionsRecommendation.getClanRecommendationsSettings());

  try {
    const url = urls.recommendationsClanSettings;
    const response = await get<IApiGetClanRecommendationsSettingsResponse>(url);

    const settings = {
      languages: response.languages,
      purpose: PurposesMapReversed[response.purpose] || PURPOSES.NO_OBLIGATIONS,
      isRecommendable: response.isRecommendable,
      isVoiceChatRequired: response.isVoiceChatRequired,
    };
    dispatch(actionsRecommendation.getClanRecommendationsSettingsResponse(settings));
  } catch (error) {
    sendErrorNotification();
  }
};

export const setClanRecommendationsSettingsThunk =
  (data: ClanRecommendationsSettings): AppAsyncThunk =>
  async (dispatch) => {
    dispatch(actionsRecommendation.setClanRecommendationsSettings());

    try {
      const url = urls.recommendationsClanSettings;
      const payload: IApiGetClanRecommendationsSettingsPayload = {
        languages: data.languages,
        purpose: PurposesMap[data.purpose] || 0,
        isRecommendable: data.isRecommendable,
        isVoiceChatRequired: data.isVoiceChatRequired,
      };
      const response = await put<IApiGetClanRecommendationsSettingsResponse>(url, payload);

      const settings = {
        languages: response.languages,
        purpose: PurposesMapReversed[response.purpose] || PURPOSES.NO_OBLIGATIONS,
        isRecommendable: response.isRecommendable,
        isVoiceChatRequired: response.isVoiceChatRequired,
      };
      dispatch(actionsRecommendation.setClanRecommendationsSettingsResponse(settings));
    } catch (error) {
      sendErrorNotification();
    }
  };

export const getRecommendationsThunk =
  (purpose: PURPOSES): AppAsyncThunk =>
  async (dispatch) => {
    dispatch(actionsRecommendation.getRecommendations());

    try {
      const url = `${urls.recommendationsClans}?purpose=${PurposesMap[purpose] || 0}`;
      const response = await get<IApiGetRecommendationsResponse>(url);

      dispatch(actionsRecommendation.getRecommendationsResponse(response.items));
    } catch (error) {
      sendErrorNotification();
    }
  };

export const getLeftApplicationCountThunk = (): AppAsyncThunk => async (dispatch) => {
  dispatch(actionsRecommendation.getLeftApplicationCount());

  try {
    const url = `${urls.recommendationsClans}left_application_count/`;
    const response = await get<IApiGetLeftApplicationCountResponse>(url);

    dispatch(actionsRecommendation.getLeftApplicationCountResponse(response.leftApplicationCount));
  } catch (error) {
    sendErrorNotification();
  }
};

export const sendApplicationThunk =
  (clan: ClanRecommendation, info: string): AppAsyncThunk =>
  async (dispatch) => {
    dispatch(actionsRecommendation.sendApplication());

    try {
      const url = `${urls.recommendationsClans}application/`;
      const payload: IApiClanRecSendApplicationPayload = { clanId: clan.id, comment: info };
      await post<IApiClanRecSendApplicationResponse>(url, payload);

      sendSendApplicationNotification({
        name: clan.name,
        tag: clan.tag,
        color: clan.color,
      });
      dispatch(actionsRecommendation.sendApplicationResponse(true, clan));
    } catch (error) {
      const axiosError = error as AxiosCustomError<{ title?: string }>;
      if (axiosError?.data) {
        const reason = axiosError.data?.title?.toLowerCase();
        if (reason && reason in SEND_APPLICATION_ERRORS) {
          sendSendApplicationErrorNotification(reason as keyof typeof SEND_APPLICATION_ERRORS, clan);
        }
        sendSendApplicationErrorNotification('default', clan);
      } else {
        sendErrorNotification();
      }
      dispatch(actionsRecommendation.sendApplicationResponse(false, clan));
    }
  };
